<template>
  <div class="page-container">
    <el-card shadow="never" class="page-card">
      <div class="filter-bar">
        <div class="filter-group">
          <el-select v-model="query.status" placeholder="全部状态" clearable class="filter-select">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </div>
        <div class="action-group">
          <el-button type="primary" icon="el-icon-plus" @click="openAdd">新增分类</el-button>
          <el-button icon="el-icon-refresh" @click="refreshList">刷新</el-button>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty v-else-if="!loading && tableData.length === 0" description="暂无分类数据" />

      <div v-else>
        <el-table
          :data="tableData"
          border
          stripe
          v-loading="loading"
          element-loading-text="加载中..."
          row-key="id"
        >
          <el-table-column prop="id" label="ID" width="72" align="center" />
          <el-table-column label="图片" width="76" align="center">
            <template slot-scope="scope">
              <el-image
                v-if="rowImageUrl(scope.row)"
                :key="scope.row.id + '-' + rowImageUrl(scope.row)"
                :src="rowImageUrl(scope.row)"
                fit="cover"
                style="width:44px;height:44px;border-radius:4px;"
                :preview-src-list="[rowImageUrl(scope.row)]"
              >
                <div slot="placeholder" class="img-placeholder"><i class="el-icon-loading"></i></div>
                <div slot="error" class="img-error"><i class="el-icon-picture-outline"></i></div>
              </el-image>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="名称" min-width="120" show-overflow-tooltip />
          <el-table-column label="排序" width="88" align="center">
            <template slot-scope="scope">
              {{ scope.row.sortOrder != null ? scope.row.sortOrder : scope.row.sort_order }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="88" align="center">
            <template slot-scope="scope">
              <el-tag v-if="rowStatus(scope.row) === 1" type="success" size="small">启用</el-tag>
              <el-tag v-else type="info" size="small">禁用</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="168" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.createdTime || scope.row.created_time) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" icon="el-icon-edit" @click="openEdit(scope.row)">编辑</el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <el-dialog
      :title="dialogMode === 'add' ? '新增分类' : '编辑分类'"
      :visible.sync="dialogVisible"
      width="480px"
      center
      append-to-body
    >
      <el-form ref="catForm" :model="form" :rules="rules" label-width="96px">
        <el-form-item label="名称" prop="name">
          <el-input v-model.trim="form.name" maxlength="50" show-word-limit placeholder="分类名称" />
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number
            v-model="form.sortOrder"
            :min="0"
            :max="999999"
            :step="1"
            controls-position="right"
            style="width:100%;"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="分类图片">
          <div class="upload-row">
            <el-upload
              class="image-uploader"
              action="#"
              :auto-upload="false"
              :show-file-list="false"
              accept="image/*"
              :on-change="onCategoryFileChange"
              :before-upload="beforeImageUpload"
            >
              <img v-if="displayImageUrl" :src="displayImageUrl" class="image-uploader-thumb" alt="" />
              <i v-else class="el-icon-plus image-uploader-icon" />
            </el-upload>
            <el-button v-if="displayImageUrl" type="text" class="clear-img" @click="clearCategoryImage">清除图片</el-button>
          </div>
          <p class="upload-tip">选择图片后点击"确定"上传，仅图片，最大 10MB</p>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { get, post, put, del, uploadShopImage } from '@/utils/http'
import { mallApi } from '@/api/mall'

export default {
  name: 'GoodsCategoryManagement',
  data() {
    return {
      loading: false,
      error: '',
      tableData: [],
      query: {
        status: null
      },
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogMode: 'add',
      submitLoading: false,
      pendingFile: null,      // 待上传的文件对象
      previewUrl: '',         // 本地预览 URL
      form: {
        id: null,
        name: '',
        sortOrder: 0,
        status: 1,
        imageUrl: ''
      },
      rules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        sortOrder: [{ required: true, message: '请输入排序', trigger: 'change' }]
      }
    }
  },
  computed: {
    displayImageUrl() {
      return this.previewUrl || this.form.imageUrl
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    rowStatus(row) {
      if (row.status === 0 || row.status === 1) return row.status
      if (row.status != null) return Number(row.status)
      return 1
    },
    rowImageUrl(row) {
      return row.imageUrl || row.image_url || ''
    },
    beforeImageUpload(file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp']
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp']

      if (!file.type || !allowedTypes.includes(file.type.toLowerCase())) {
        this.$message.error('不支持的文件类型，仅支持：jpg、jpeg、png、gif、webp、bmp')
        return false
      }

      const fileName = file.name.toLowerCase()
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext))
      if (!hasValidExtension) {
        this.$message.error('文件名无效，请确保文件有正确的扩展名')
        return false
      }

      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('图片大小不能超过10MB')
        return false
      }
      return true
    },
    onCategoryFileChange(file) {
      // 选文件后仅做本地预览，不立即上传
      this.pendingFile = file.raw
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.previewUrl = URL.createObjectURL(file.raw)
    },
    clearCategoryImage() {
      this.pendingFile = null
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = ''
      }
      this.form.imageUrl = ''
    },
    async fetchList() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }
        if (this.query.status != null && this.query.status !== '') params.status = this.query.status

        const res = await get(mallApi.categoryPage, params)
        const data = res.data || {}
        this.tableData = Array.isArray(data.records) ? data.records : []
        this.pagination.total = typeof data.total === 'number' ? data.total : 0
        if (data.current) this.pagination.pageNum = data.current
      } catch (e) {
        console.error(e)
        this.error = e.message || '加载分类失败'
        this.tableData = []
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchList()
    },
    refreshList() {
      this.query = { status: null }
      this.pagination.pageNum = 1
      this.fetchList()
    },
    handleCurrentChange(page) {
      this.pagination.pageNum = page
      this.fetchList()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.pageNum = 1
      this.fetchList()
    },
    openAdd() {
      this.dialogMode = 'add'
      this.form = { id: null, name: '', sortOrder: 0, status: 1, imageUrl: '' }
      this.clearCategoryImage()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.catForm && this.$refs.catForm.clearValidate())
    },
    openEdit(row) {
      this.dialogMode = 'edit'
      this.clearCategoryImage()
      this.form = {
        id: row.id,
        name: row.name || '',
        sortOrder: row.sortOrder != null ? row.sortOrder : row.sort_order || 0,
        status: this.rowStatus(row),
        imageUrl: this.rowImageUrl(row)
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.catForm && this.$refs.catForm.clearValidate())
    },
    submitForm() {
      this.$refs.catForm.validate(async valid => {
        if (!valid) return
        this.submitLoading = true
        try {
          // 如果有待上传的新文件，先上传
          if (this.pendingFile) {
            const url = await uploadShopImage(mallApi.categoryUploadImage, this.pendingFile)
            this.form.imageUrl = url
            this.pendingFile = null
            if (this.previewUrl) {
              URL.revokeObjectURL(this.previewUrl)
              this.previewUrl = ''
            }
          }
          const payload = {
            name: this.form.name,
            sortOrder: this.form.sortOrder,
            status: this.form.status
          }
          if (this.form.imageUrl) payload.imageUrl = this.form.imageUrl
          if (this.dialogMode === 'add') {
            await post(mallApi.categoryAdd, payload)
            this.$message.success('新增成功')
          } else {
            payload.id = this.form.id
            await put(mallApi.categoryUpdate, payload)
            this.$message.success('更新成功')
          }
          this.dialogVisible = false
          await this.fetchList()
        } catch (e) {
          this.$message.error(e.message || '提交失败')
        } finally {
          this.submitLoading = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(`确定删除分类「${row.name}」？`, '提示', { type: 'warning' })
        .then(async () => {
          await del(mallApi.categoryDelete(row.id))
          this.$message.success('已删除')
          if (this.tableData.length === 1 && this.pagination.pageNum > 1) {
            this.pagination.pageNum -= 1
          }
          await this.fetchList()
        })
        .catch(() => {})
    },
    formatDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return String(val)
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}`
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 16px;
}
.page-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: #fafafa;
  border-radius: 6px;
  margin-bottom: 16px;
}
.filter-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.filter-select {
  width: 140px;
}
.action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.error {
  color: #f56c6c;
  padding: 16px;
  text-align: center;
}
.pagination-container {
  margin-top: 16px;
  text-align: right;
}
.upload-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.clear-img {
  padding: 0;
  margin-bottom: 8px;
}
.upload-tip {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
}
.image-uploader ::v-deep .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
}
.image-uploader ::v-deep .el-upload:hover {
  border-color: #409eff;
}
.image-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 120px;
  height: 120px;
  line-height: 120px;
  text-align: center;
  display: block;
}
.image-uploader-thumb {
  width: 120px;
  height: 120px;
  display: block;
  object-fit: cover;
}
.img-placeholder,
.img-error {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  border-radius: 4px;
  color: #dcdfe6;
  font-size: 18px;
}
</style>
